'use client';

import {
  forwardRef,
  ForwardedRef,
  HTMLAttributes,
  useState,
  useEffect,
  useRef,
} from 'react';
import classNames from 'classnames';
import './css/Avatar.scss';
import { EmptySvg } from './EmptySvg';
import { getInitials } from './utils/getInitials';

type AvatarOrientation = 'horizontal' | 'vertical';
type AvatarSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';
type AvatarVariant = 'picture' | 'initials' | 'empty';
type AvatarImageStatus = 'pending' | 'loaded' | 'errored';

export interface AvatarProps extends HTMLAttributes<HTMLDivElement> {
  name?: string;
  onError?(): void;
  orientation?: AvatarOrientation;
  size?: AvatarSize;
  showLabel?: boolean;
  src?: string;
  variant?: AvatarVariant;
  imageLabel?: string;
}

export const Avatar = forwardRef(
  (props: AvatarProps, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      name,
      onError,
      orientation = 'horizontal',
      size = 'md',
      showLabel = true,
      src,
      variant = src ? 'picture' : name ? 'initials' : 'empty',
      imageLabel = '',
      className,
      ...spreadProps
    } = props;

    const [initials, setInitials] = useState<string>('');
    const [status, setStatus] = useState<AvatarImageStatus>('pending');
    // The image source is stored in state so that it can force img onload to rerun.
    const [imageSrc, setImageSrc] = useState<string | undefined>();

    const svgRef = useRef<SVGSVGElement>(null);

    useEffect(() => setInitials(getInitials(name)), [name]);

    // Sets the status back to pending if the src path changes.
    useEffect(() => setStatus('pending'), [src]);

    // Sets the image source to the new src path.
    useEffect(() => setImageSrc(src), [src]);

    const handleError = () => {
      setStatus('errored');

      if (onError) {
        onError();
      }
    };

    const handleLoad = () => setStatus('loaded');

    const getIconContents = () => {
      const isValidPictureVariant =
        variant === 'picture' && src && status !== 'errored';
      const isInvalidPictureVariant =
        variant === 'picture' && (!src || status === 'errored');
      const isValidInitialsVariant = variant === 'initials' && initials;

      const initialsToRender = size === 'sm' ? initials[0] : initials;

      if (isValidPictureVariant) {
        return (
          <>
            {/* Renders either initials or a silhouette behind the image until it's loaded. */}
            {initials.length ? (
              <div className={initialsClasses}>{initialsToRender}</div>
            ) : (
              <EmptySvg
                ref={svgRef}
                label={imageLabel}
              />
            )}
            <img
              className={imageClasses}
              src={imageSrc}
              alt={imageLabel}
              onError={handleError}
              onLoad={handleLoad}
            />
          </>
        );
      }

      if (isInvalidPictureVariant || isValidInitialsVariant) {
        return (
          <div className="ps-avatar-icon-initials">{initialsToRender}</div>
        );
      }

      return <EmptySvg label={imageLabel} />;
    };

    const classes = classNames({
      'ps-avatar': true,
      [`ps-avatar-${orientation}`]: true,
      [`ps-avatar-${size}`]: true,
      [`${className}`]: className,
    });

    const imageClasses = classNames({
      'ps-avatar-icon-image': true,
      'ps-avatar-icon-image--hidden': status !== 'loaded',
    });

    const initialsClasses = classNames({
      'ps-avatar-icon-initials': true,
      'ps-avatar-icon-initials--hidden': status === 'loaded',
    });

    return (
      <div
        className={classes}
        ref={ref}
        {...spreadProps}
      >
        <div className="ps-avatar-icon">{getIconContents()}</div>

        {name && showLabel && <div className="ps-avatar-label">{name}</div>}
      </div>
    );
  }
);
